import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import ChildIssuesIcon from '@atlaskit/icon/core/child-issues';
import ChildIssuesIconOld from '@atlaskit/icon/glyph/child-issues';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

type Props = {
	numTotalChildren: number;
	numCompleteChildren: number;
	onClickThrough: (e: MouseEvent) => void;
};
export const ChildIssuesMetadata = ({
	numTotalChildren,
	numCompleteChildren,
	onClickThrough,
}: Props) => {
	const { formatMessage } = useIntl();

	if (!numTotalChildren) {
		return null;
	}

	const message = formatMessage(
		expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.childIssuesMetadataTooltipIssueTermRefresh
			: messages.childIssuesMetadataTooltip,
		{
			complete: numCompleteChildren,
			total: numTotalChildren,
		},
	);

	return (
		<ChildIssuesMetadataWrapper
			tabIndex={-1}
			onClick={onClickThrough}
			data-testid="software-backlog.card-list.card.card-contents.child-issues-metadata"
		>
			<ToolTip
				content={message}
				testId="software-backlog.card-list.card.card-contents.child-issues-metadata.tooltip"
			>
				{fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
					<ChildIssuesIcon
						testId="software-backlog.card-list.card.card-contents.child-issues-metadata.child-issues-icon"
						label=""
						color={token('color.icon.subtle', colors.N300)}
						LEGACY_fallbackIcon={ChildIssuesIconOld}
						LEGACY_primaryColor={token('color.icon.subtle', colors.N300)}
						LEGACY_size="small"
					/>
				) : (
					<ChildIssuesIconOld
						testId="software-backlog.card-list.card.card-contents.child-issues-metadata.child-issues-icon"
						primaryColor={token('color.icon.subtle', colors.N300)}
						size="small"
						label=""
					/>
				)}
			</ToolTip>
		</ChildIssuesMetadataWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildIssuesMetadataWrapper = styled.div({
	outline: 'none',
	zIndex: 1 /* surface the element above the interaction layer so tooltips can be triggered */,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		display: 'flex',
	},
	gridColumn: 'child-meta / span 1',
	gridRow: 'card-detail / span 1',
});
